import React, { useEffect } from 'react';
import { FeatherShoppingCart, FeatherX, FeatherTrash2 } from '@subframe/core';
import { Drawer } from '../../ui/components/Drawer';
import { Button } from '../../ui/components/Button';
import { useCartStore } from '../../store/cartStore';
import { useNotificationStore } from '../../store/notificationStore';
import { calculateCartTotals } from '../../utils/cartTotals';
import { CartItemRow } from './CartItemRow';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

export function CartDrawer({ open, onClose, onCheckout }: CartDrawerProps) {
  const { items, removeItem, clearCart, removeExpiredItems } = useCartStore();
  const { warning: showWarning } = useNotificationStore();
  const totals = calculateCartTotals(items);

  // Remove expired items on open
  useEffect(() => {
    if (open) {
      const expired = removeExpiredItems();
      if (expired.length > 0) {
        showWarning(
          `Removed ${expired.length} expired item${expired.length !== 1 ? 's' : ''} from cart`
        );
      }
    }
  }, [open]);

  const handleCheckout = () => {
    onClose();
    onCheckout();
  };

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Drawer.Content className="w-[400px] mobile:w-full mobile:min-w-0 max-w-full !gap-0 !items-stretch">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <FeatherShoppingCart className="h-5 w-5 text-brand-600" />
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              Cart ({items.length} item{items.length !== 1 ? 's' : ''})
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-neutral-100 transition-colors"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 p-8">
              <FeatherShoppingCart className="h-12 w-12 text-neutral-300" />
              <p className="text-body font-body text-subtext-color text-center">
                Your cart is empty
              </p>
              <p className="text-caption font-caption text-subtext-color text-center">
                Browse NFTs and click the cart icon to add items
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-border">
              {items.map((item) => (
                <CartItemRow
                  key={item.nft_token_id}
                  item={item}
                  onRemove={removeItem}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="flex-shrink-0 border-t border-solid border-neutral-border p-4 w-full">
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex justify-between items-baseline gap-4">
                <span className="text-body font-body text-subtext-color">Subtotal</span>
                <span className="text-body font-body text-default-font whitespace-nowrap">
                  {totals.subtotal.toFixed(2)} XRP
                </span>
              </div>
              {totals.feeBreakdown.map((entry) => (
                <div key={entry.marketplace} className="flex justify-between items-baseline gap-4">
                  <span className="text-caption font-caption text-subtext-color truncate">
                    {entry.marketplace} fee ({entry.feePercentage}% × {entry.itemCount})
                  </span>
                  <span className="text-caption font-caption text-default-font whitespace-nowrap">
                    ~{entry.feeAmount.toFixed(2)} XRP
                  </span>
                </div>
              ))}
              <div className="flex justify-between items-baseline gap-4 pt-2 border-t border-solid border-neutral-border">
                <span className="text-body-bold font-body-bold text-default-font">Total</span>
                <span className="text-heading-3 font-heading-3 text-brand-600 whitespace-nowrap">
                  {totals.total.toFixed(2)} XRP
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                variant="brand-primary"
                onClick={handleCheckout}
                className="w-full"
              >
                Checkout ({items.length} item{items.length !== 1 ? 's' : ''})
              </Button>
              <button
                onClick={clearCart}
                className="flex items-center justify-center gap-1.5 py-2 text-caption font-caption text-subtext-color hover:text-error-600 transition-colors"
              >
                <FeatherTrash2 className="h-3.5 w-3.5" />
                Clear Cart
              </button>
            </div>
          </div>
        )}
      </Drawer.Content>
    </Drawer>
  );
}
